import React, { useState, useEffect, useRef } from 'react';
import { Plus, Trash2, Pencil, X, Upload, Eye, EyeOff, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useToast } from '../../hooks/use-toast';
import adminApi from '../../lib/adminApi';
import BulkMediaUpload from '../../components/BulkMediaUpload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CATEGORIES = ['mariage', 'festival', 'concert', 'sport', 'corporate', 'club', 'produit', 'autre'];
const CHUNK_SIZE = 5 * 1024 * 1024;

const EMPTY_FORM = {
  type: 'photo',
  source: 'url',
  url: '',
  thumbnail: '',
  title: '',
  titleEn: '',
  titleAr: '',
  titleIt: '',
  category: 'autre',
  published: true,
  sortOrder: 0,
};

const resolveUrl = (url) => (url && url.startsWith('/api/') ? `${BACKEND_URL}${url}` : url);

const MediaManager = () => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const photoRef = useRef(null);
  const videoRef = useRef(null);

  const load = async () => {
    try {
      const res = await adminApi.get('/api/admin/media');
      setItems(res.data);
    } catch (e) {
      toast({ title: 'Erreur de chargement des médias', variant: 'destructive' });
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({ ...EMPTY_FORM, ...item });
    setShowForm(true);
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await adminApi.post('/api/admin/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm((p) => ({ ...p, url: res.data.url, source: 'upload' }));
      toast({ title: 'Image uploadée' });
    } catch (err) {
      toast({ title: "Erreur d'upload", description: err.response?.data?.detail || '', variant: 'destructive' });
    } finally {
      setUploading(false);
      if (photoRef.current) photoRef.current.value = '';
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setProgress(0);
    const uploadId = `v${Date.now()}${Math.random().toString(36).slice(2, 8)}`;
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    try {
      for (let i = 0; i < totalChunks; i += 1) {
        const chunk = file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
        const fd = new FormData();
        fd.append('upload_id', uploadId);
        fd.append('chunk_index', String(i));
        fd.append('file', chunk, `${i}.part`);
        // eslint-disable-next-line no-await-in-loop
        await adminApi.post('/api/admin/upload/video/chunk', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setProgress(Math.round(((i + 1) / totalChunks) * 100));
      }
      const res = await adminApi.post('/api/admin/upload/video/complete', {
        upload_id: uploadId,
        filename: file.name,
        total_chunks: totalChunks,
      });
      setForm((p) => ({ ...p, url: res.data.url, source: 'upload', type: 'video' }));
      toast({ title: 'Vidéo uploadée' });
    } catch (err) {
      toast({ title: "Erreur d'upload vidéo", description: err.response?.data?.detail || '', variant: 'destructive' });
    } finally {
      setUploading(false);
      setProgress(0);
      if (videoRef.current) videoRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.url) {
      toast({ title: 'URL ou fichier requis', variant: 'destructive' });
      return;
    }
    const payload = { ...form, sortOrder: parseInt(form.sortOrder, 10) || 0 };
    delete payload.id;
    delete payload.createdAt;
    try {
      if (editing) {
        await adminApi.put(`/api/admin/media/${editing.id}`, payload);
        toast({ title: 'Média mis à jour' });
      } else {
        await adminApi.post('/api/admin/media', payload);
        toast({ title: 'Média ajouté' });
      }
      setShowForm(false);
      load();
    } catch (err) {
      toast({ title: 'Erreur', description: err.response?.data?.detail || '', variant: 'destructive' });
    }
  };

  const togglePublished = async (item) => {
    await adminApi.put(`/api/admin/media/${item.id}`, { published: !item.published });
    load();
  };

  const remove = async (item) => {
    if (!window.confirm(`Supprimer « ${item.title || item.url} » ?`)) return;
    await adminApi.delete(`/api/admin/media/${item.id}`);
    toast({ title: 'Média supprimé' });
    load();
  };

  const preview = (item) =>
    item.type === 'video'
      ? item.thumbnail || (item.source === 'youtube' ? `https://img.youtube.com/vi/${item.url}/sddefault.jpg` : '')
      : resolveUrl(item.url);

  return (
    <div data-testid="admin-media-manager">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white">Galerie — Photos & Vidéos</h1>
          <p className="text-gray-400 text-sm mt-1">{items.length} média(s). Modifiez ou supprimez les contenus pré-remplis.</p>
        </div>
        <Button onClick={openNew} className="bg-orange-600 hover:bg-orange-500" data-testid="media-add-btn">
          <Plus className="w-4 h-4 mr-2" /> Ajouter
        </Button>
      </div>

      <BulkMediaUpload onDone={load} />

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-gray-900 border border-gray-800 rounded-xl p-6 mb-8 space-y-4" data-testid="media-form">
          <div className="flex items-center justify-between">
            <h2 className="text-white font-semibold">{editing ? 'Modifier le média' : 'Nouveau média'}</h2>
            <button type="button" onClick={() => setShowForm(false)} className="text-gray-400 hover:text-white" data-testid="media-form-close">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <Label className="text-gray-300">Type</Label>
              <select
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
                className="w-full mt-1 bg-gray-800 border border-gray-700 text-white rounded-md px-3 py-2 text-sm"
                data-testid="media-type"
              >
                <option value="photo">Photo</option>
                <option value="video">Vidéo</option>
              </select>
            </div>
            <div>
              <Label className="text-gray-300">Source</Label>
              <select
                value={form.source}
                onChange={(e) => setForm({ ...form, source: e.target.value })}
                className="w-full mt-1 bg-gray-800 border border-gray-700 text-white rounded-md px-3 py-2 text-sm"
                data-testid="media-source"
              >
                <option value="url">Lien externe</option>
                <option value="upload">Fichier uploadé</option>
                <option value="youtube">YouTube</option>
              </select>
            </div>
            <div>
              <Label className="text-gray-300">Catégorie</Label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full mt-1 bg-gray-800 border border-gray-700 text-white rounded-md px-3 py-2 text-sm"
                data-testid="media-category"
              >
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div>
            <Label className="text-gray-300">
              {form.source === 'youtube' ? 'ID de la vidéo YouTube (ex: bKkXO8078V0)' : 'URL du média'}
            </Label>
            <Input
              value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value })}
              placeholder={form.source === 'youtube' ? 'bKkXO8078V0' : 'https://... ou /api/uploads/...'}
              className="mt-1 bg-gray-800 border-gray-700 text-white"
              data-testid="media-url"
            />
          </div>

          <div className="flex flex-wrap gap-3">
            <input ref={photoRef} type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" />
            <Button type="button" variant="outline" disabled={uploading} onClick={() => photoRef.current?.click()} className="border-gray-700 text-gray-300" data-testid="media-upload-photo">
              <Upload className="w-4 h-4 mr-2" /> Uploader une photo
            </Button>
            <input ref={videoRef} type="file" accept="video/mp4,video/webm,video/quicktime" onChange={handleVideoUpload} className="hidden" />
            <Button type="button" variant="outline" disabled={uploading} onClick={() => videoRef.current?.click()} className="border-gray-700 text-gray-300" data-testid="media-upload-video">
              <Upload className="w-4 h-4 mr-2" /> Uploader une vidéo (max 100 Mo)
            </Button>
            {uploading && (
              <span className="flex items-center text-sm text-orange-400" data-testid="media-upload-progress">
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> {progress ? `${progress}%` : 'Envoi...'}
              </span>
            )}
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-gray-300">Titre (FR)</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 bg-gray-800 border-gray-700 text-white" data-testid="media-title-fr" />
            </div>
            <div>
              <Label className="text-gray-300">Titre (EN)</Label>
              <Input value={form.titleEn} onChange={(e) => setForm({ ...form, titleEn: e.target.value })} className="mt-1 bg-gray-800 border-gray-700 text-white" data-testid="media-title-en" />
            </div>
            <div>
              <Label className="text-gray-300">Titre (AR)</Label>
              <Input value={form.titleAr} onChange={(e) => setForm({ ...form, titleAr: e.target.value })} className="mt-1 bg-gray-800 border-gray-700 text-white" data-testid="media-title-ar" />
            </div>
            <div>
              <Label className="text-gray-300">Titre (IT)</Label>
              <Input value={form.titleIt} onChange={(e) => setForm({ ...form, titleIt: e.target.value })} className="mt-1 bg-gray-800 border-gray-700 text-white" data-testid="media-title-it" />
            </div>
            <div>
              <Label className="text-gray-300">Miniature (optionnel)</Label>
              <Input value={form.thumbnail} onChange={(e) => setForm({ ...form, thumbnail: e.target.value })} className="mt-1 bg-gray-800 border-gray-700 text-white" data-testid="media-thumbnail" />
            </div>
            <div>
              <Label className="text-gray-300">Ordre d'affichage</Label>
              <Input type="number" value={form.sortOrder} onChange={(e) => setForm({ ...form, sortOrder: e.target.value })} className="mt-1 bg-gray-800 border-gray-700 text-white" data-testid="media-sort" />
            </div>
          </div>

          <label className="flex items-center gap-2 text-gray-300 text-sm">
            <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="media-published" />
            Visible sur le site
          </label>

          <Button type="submit" className="bg-orange-600 hover:bg-orange-500" data-testid="media-save">
            {editing ? 'Enregistrer' : 'Ajouter'}
          </Button>
        </form>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="media-list">
        {items.map((item) => (
          <div key={item.id} className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden" data-testid={`media-card-${item.id}`}>
            <div className="aspect-square bg-black">
              {preview(item) ? (
                <img src={preview(item)} alt={item.title} loading="lazy" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-600 text-xs">Vidéo</div>
              )}
            </div>
            <div className="p-3">
              <p className="text-white text-xs font-medium truncate">{item.title || item.url}</p>
              <p className="text-gray-500 text-[10px] uppercase tracking-wider mt-0.5">{item.type} · {item.category}</p>
              {item.flaggedText && (
                <p className="text-amber-400 text-[10px] mt-1" data-testid={`media-flagged-${item.id}`}>
                  Texte ou logo détecté sur le visuel
                </p>
              )}
              {item.isVideoThumb && (
                <p className="text-gray-500 text-[10px] mt-1">Miniature d'une vidéo (masquée)</p>
              )}
              <div className="flex gap-1 mt-2">
                <button onClick={() => openEdit(item)} className="p-1.5 rounded bg-gray-800 text-gray-300 hover:text-white" data-testid={`media-edit-${item.id}`}>
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => togglePublished(item)} className="p-1.5 rounded bg-gray-800 text-gray-300 hover:text-white" data-testid={`media-toggle-${item.id}`}>
                  {item.published ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                </button>
                <button onClick={() => remove(item)} className="p-1.5 rounded bg-gray-800 text-red-400 hover:text-red-300" data-testid={`media-delete-${item.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MediaManager;
